import { API } from "../config/Api";

const GET = async (url: any, params: any) => {
  return new Promise(async (resolve, reject) => {
    fetch(API.BASE_URL + url, {
      method: 'get',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(json => {
        resolve(json);
      })
      .catch(error => {
        reject(error);
      });
  });
};

const POST = async (url: any, body: any) => {
  return new Promise(async (resolve, reject) => {
    fetch(API.BASE_URL + url, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(response => response.json())
      .then(json => {
        resolve(json);
      })
      .catch(error => {
        reject(error);
      });
  });
};

const PUT = async (url: any, body: any) => {
  return new Promise(async (resolve, reject) => {
    fetch(API.BASE_URL + url, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(response => response.json())
      .then(json => {
        resolve(json);
      })
      .catch(error => {
        reject(error);
      });
  });
};

const DELETE = async (url: any) => {
  return new Promise(async (resolve, reject) => {
    fetch(API.BASE_URL + url, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(json => {
        resolve(json);
      })
      .catch(error => {
        reject(error);
      });
  });
};

const COMPRESS_IMAGE = async (file: File) => {
  try {
    if (!file) return Promise.reject(new Error("No Image Is selected.."));
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${API.BASE_URL}${API.IMAGE_COMPRESS}`, {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    if (!response?.ok)
      return Promise.reject(
        new Error(data?.message ?? "Something went wrong..")
      );
    return { ...data, url: data.Location, status: true };
  } catch (err: any) {
    return Promise.reject(new Error(err.message));
  }
};

// New WhatsApp API utility functions
const WHATSAPP_API = {
  // Generic WhatsApp API call with error handling
  call: async (endpoint: string, options: RequestInit = {}) => {
    try {
      const response = await fetch(`${API.WHATSAPP_SERVER_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error types
        if (response.status === 400 && data.error === "Invalid recipient") {
          throw new Error(`Invalid recipient: ${data.message}`);
        } else if (response.status === 408) {
          throw new Error(`Request timed out: ${data.message}`);
        } else if (data.error === "Duplicate request") {
          throw new Error(`Duplicate request: ${data.message}`);
        } else if (data.suggestion && data.suggestion.includes('force=true')) {
          throw new Error(`Session issue detected: ${data.message}. Use force parameter.`);
        } else {
          throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
        }
      }

      return { data, response };
    } catch (error: any) {
      throw new Error(error.message || 'WhatsApp API call failed');
    }
  },

  // Send WhatsApp message
  sendMessage: async (sessionId: string, number: string, message: string, force = false) => {
    return WHATSAPP_API.call('/whatsapp', {
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        number,
        message,
        force,
      }),
    });
  },

  // Validate WhatsApp numbers
  validateNumbers: async (sessionId: string, numbers: string[]) => {
    return WHATSAPP_API.call('/validate-numbers', {
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        numbers,
      }),
    });
  },

  // Get session status
  getStatus: async (sessionId: string) => {
    return WHATSAPP_API.call(`/status?sessionId=${sessionId}`);
  },

  // Reconnect session
  reconnect: async (sessionId: string, force = false) => {
    return WHATSAPP_API.call('/reconnect', {
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        force,
      }),
    });
  },

  // Initialize session
  initSession: async (sessionData: any) => {
    return WHATSAPP_API.call('/init-session', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  },

  // Reset session
  resetSession: async (sessionId: string) => {
    return WHATSAPP_API.call('/reset-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  },

  // Disconnect session
  disconnect: async (sessionId: string) => {
    return WHATSAPP_API.call('/disconnect', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  },

  // Get QR code
  getQR: async (sessionId: string) => {
    return WHATSAPP_API.call(`/qr?sessionId=${sessionId}`);
  },
};

export { GET, POST, PUT, DELETE, COMPRESS_IMAGE, WHATSAPP_API };